/**
 * ReviewViewer — Renders a review JSON document from review-api.
 *
 * Expects post.content to be a JSON string with structure:
 * { review: {...}, findings: [...], deliverables: [...], meta: {...} }
 */

import { useMemo, useState } from 'react';
import { parseMarkdown } from '../utils/markdownConfig'

interface ReviewData {
  review: {
    id: number;
    subject_type: string;
    subject_title: string;
    subject_url: string | null;
    perspectives: string[];
    goal: string;
    context: string | null;
    status: string;
    overall_score: number | null;
    summary: string | null;
    priority_actions: Array<{
      title: string;
      recommendation: string;
      perspective: string;
      severity: string;
      impact: number;
      effort: number;
    }>;
    started_at: string | null;
    completed_at: string | null;
    created_at: string | null;
  };
  findings: Array<{
    id: number;
    perspective: string;
    category: string;
    severity: string;
    title: string;
    description: string;
    recommendation: string | null;
    location: string | null;
    impact_score: number | null;
    effort_score: number | null;
    confidence: number | null;
    ai_model: string | null;
  }>;
  deliverables: Array<{
    id: number;
    deliverable_type: string;
    title: string;
    content: string;
    perspective: string;
    ai_model: string | null;
  }>;
  screenshots: Record<string, string>;
  meta: {
    version: string;
    generated_at: string;
    generator: string;
  };
}

const SEVERITY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  critical: { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },
  warning: { bg: '#fffbeb', border: '#fcd34d', text: '#d97706' },
  info: { bg: '#eff6ff', border: '#93c5fd', text: '#2563eb' },
  positive: { bg: '#f0fdf4', border: '#86efac', text: '#16a34a' },
};

const SEVERITY_ICONS: Record<string, string> = {
  critical: '🔴',
  warning: '🟡',
  info: '🔵',
  positive: '🟢',
};

function ScoreCircle({ score }: { score: number | null | undefined }) {
  if (score == null) return null;
  const rounded = Math.round(score);
  const color = rounded >= 70 ? '#16a34a' : rounded >= 40 ? '#d97706' : '#dc2626';
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (rounded / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}>
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="50" cy="50" r="40" fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          transform="rotate(-90 50 50)"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 28, fontWeight: 700, color,
      }}>
        {rounded}
      </div>
    </div>
  );
}

function FindingCard({ finding }: { finding: ReviewData['findings'][0] }) {
  const colors = SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.info;
  const icon = SEVERITY_ICONS[finding.severity] || '⚪';
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        borderLeft: `4px solid ${colors.border}`,
        background: colors.bg,
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 8,
        cursor: 'pointer',
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{icon}</span>
        <span style={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          color: colors.text, padding: '2px 8px',
          background: `${colors.border}40`, borderRadius: 4,
        }}>
          {finding.severity}
        </span>
        <span style={{ fontSize: 13, color: '#6b7280' }}>{finding.perspective}</span>
        {finding.location && (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>📍 {finding.location}</span>
        )}
      </div>
      <div style={{ fontWeight: 600, marginTop: 6, fontSize: 14 }}>{finding.title}</div>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: '#374151' }}>{finding.description}</p>
          {finding.recommendation && (
            <div style={{
              marginTop: 8, padding: '8px 12px',
              background: 'rgba(255,255,255,0.7)', borderRadius: 6,
              fontSize: 13, color: '#1f2937',
            }}>
              <strong>→ </strong>{finding.recommendation}
            </div>
          )}
          {(finding.impact_score || finding.effort_score) && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
              {finding.impact_score && `Impact: ${finding.impact_score}/10`}
              {finding.impact_score && finding.effort_score && ' · '}
              {finding.effort_score && `Effort: ${finding.effort_score}/10`}
              {finding.confidence && ` · Confidence: ${Math.round(finding.confidence * 100)}%`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ReviewViewerProps {
  content: string;
}

export function ReviewViewer({ content }: ReviewViewerProps) {
  const [tab, setTab] = useState<'findings' | 'deliverables'>('findings');
  const [filter, setFilter] = useState('all');
  const [expandedDeliverable, setExpandedDeliverable] = useState<number | null>(null);

  const data = useMemo<ReviewData | null>(() => {
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }, [content]);

  if (!data || !data.review) {
    return (
      <div style={{ padding: 20 }}>
        <p>Review-Dokument konnte nicht geparst werden.</p>
        <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: 400 }}>{content}</pre>
      </div>
    );
  }

  const { review, findings, deliverables } = data;
  const perspectives = [...new Set(findings.map(f => f.perspective))];
  const filtered = filter === 'all' ? findings : findings.filter(f => f.perspective === filter);

  const severityCounts = {
    critical: findings.filter(f => f.severity === 'critical').length,
    warning: findings.filter(f => f.severity === 'warning').length,
    info: findings.filter(f => f.severity === 'info').length,
    positive: findings.filter(f => f.severity === 'positive').length,
  };

  const formatDate = (d: string | null) => d
    ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header with Score */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 24 }}>
        <ScoreCircle score={review.overall_score} />
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>{review.subject_title}</h2>
          {review.subject_url && (
            <a href={review.subject_url} target="_blank" rel="noopener noreferrer"
              style={{ color: '#2563eb', fontSize: 13 }}>
              {review.subject_url}
            </a>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', fontSize: 12 }}>
            <span style={{ padding: '2px 8px', background: '#dbeafe', borderRadius: 4, color: '#1e40af' }}>
              {review.subject_type}
            </span>
            <span style={{ padding: '2px 8px', background: '#f3e8ff', borderRadius: 4, color: '#7c3aed' }}>
              Goal: {review.goal}
            </span>
            {review.perspectives.map(p => (
              <span key={p} style={{ padding: '2px 8px', background: '#f1f5f9', borderRadius: 4, color: '#475569' }}>
                {p}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            {formatDate(review.completed_at || review.created_at)}
          </div>
        </div>
      </div>

      {/* Severity Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {Object.entries(severityCounts).filter(([, c]) => c > 0).map(([sev, count]) => {
          const colors = SEVERITY_COLORS[sev] || SEVERITY_COLORS.info;
          return (
            <div key={sev} style={{
              padding: '8px 16px', borderRadius: 8,
              background: colors.bg, border: `1px solid ${colors.border}`,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>{SEVERITY_ICONS[sev]}</span>
              <span style={{ fontWeight: 700, color: colors.text }}>{count}</span>
              <span style={{ fontSize: 12, color: colors.text }}>{sev}</span>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {review.summary && (
        <div style={{
          padding: 16, background: '#f8fafc', borderRadius: 8,
          border: '1px solid #e2e8f0', marginBottom: 20,
        }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#64748b' }}>Executive Summary</h3>
          <p style={{ margin: 0, lineHeight: 1.7, fontSize: 14 }}>{review.summary}</p>
        </div>
      )}

      {/* Priority Actions */}
      {review.priority_actions.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, marginBottom: 8 }}>Priority Actions</h3>
          {review.priority_actions.map((a, i) => {
            const colors = SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.info;
            return (
              <div key={i} style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 6,
                background: colors.bg, borderLeft: `4px solid ${colors.border}`,
              }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {SEVERITY_ICONS[a.severity]} {a.title}
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#4b5563' }}>
                  {a.recommendation}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setTab('findings')}
          style={{
            padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: tab === 'findings' ? '#2563eb' : '#f1f5f9',
            color: tab === 'findings' ? '#fff' : '#475569',
            fontWeight: 500, fontSize: 13,
          }}
        >
          Findings ({findings.length})
        </button>
        {deliverables.length > 0 && (
          <button
            onClick={() => setTab('deliverables')}
            style={{
              padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: tab === 'deliverables' ? '#2563eb' : '#f1f5f9',
              color: tab === 'deliverables' ? '#fff' : '#475569',
              fontWeight: 500, fontSize: 13,
            }}
          >
            Deliverables ({deliverables.length})
          </button>
        )}
      </div>

      {/* Findings Tab */}
      {tab === 'findings' && (
        <>
          {perspectives.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => setFilter('all')}
                style={{
                  padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb',
                  background: filter === 'all' ? '#1e293b' : '#fff',
                  color: filter === 'all' ? '#fff' : '#475569',
                  fontSize: 12, cursor: 'pointer',
                }}
              >
                All ({findings.length})
              </button>
              {perspectives.map(p => (
                <button
                  key={p}
                  onClick={() => setFilter(p)}
                  style={{
                    padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb',
                    background: filter === p ? '#1e293b' : '#fff',
                    color: filter === p ? '#fff' : '#475569',
                    fontSize: 12, cursor: 'pointer',
                  }}
                >
                  {p.replace('_', ' ')} ({findings.filter(f => f.perspective === p).length})
                </button>
              ))}
            </div>
          )}
          {filtered.map((f, i) => <FindingCard key={i} finding={f} />)}
        </>
      )}

      {/* Deliverables Tab */}
      {tab === 'deliverables' && deliverables.map(d => (
        <div key={d.id} style={{
          borderRadius: 8, border: '1px solid #e2e8f0',
          marginBottom: 8, overflow: 'hidden',
        }}>
          <div
            onClick={() => setExpandedDeliverable(expandedDeliverable === d.id ? null : d.id)}
            style={{
              padding: '12px 16px', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: expandedDeliverable === d.id ? '#f8fafc' : '#fff',
            }}
          >
            <div>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{d.title}</span>
              <span style={{
                marginLeft: 8, fontSize: 11, padding: '2px 8px',
                background: '#dbeafe', borderRadius: 4, color: '#1e40af',
              }}>
                {d.deliverable_type}
              </span>
            </div>
            <span style={{ color: '#9ca3af' }}>{expandedDeliverable === d.id ? '▼' : '▶'}</span>
          </div>
          {expandedDeliverable === d.id && (
            <div
              style={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}
              className="markdown-body"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(d.content) }}
            />
          )}
        </div>
      ))}

      {/* Screenshots */}
      {data.screenshots && Object.keys(data.screenshots).length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 14, marginBottom: 12 }}>📸 Screenshots</h3>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {Object.entries(data.screenshots).map(([name, url]) => (
              <div key={name} style={{ flex: '1 1 300px', maxWidth: 500 }}>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: '#64748b',
                  marginBottom: 4, textTransform: 'capitalize',
                }}>
                  {name}
                </div>
                <a href={url as string} target="_blank" rel="noopener noreferrer">
                  <img
                    src={url as string}
                    alt={`Screenshot ${name}`}
                    style={{
                      width: '100%', borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                  />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Meta */}
      <div style={{ marginTop: 24, fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
        Generated by {data.meta?.generator} · {formatDate(data.meta?.generated_at)} · v{data.meta?.version}
      </div>
    </div>
  );
}
